import {
  pledgeStudyConfigControllerListMeasurementPoints,
  pledgeStudyConfigControllerListPledgeStudyTypes,
  pledgeStudyControllerDeleteAttachment,
  pledgeStudyControllerUpdate,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  BookMarked,
  CloudUpload,
  Download,
  FileText,
  Image,
  Lock,
  Minus,
  Plus,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { FormSection } from '../../../components/forms/form-section';
import { AppLayout } from '../../../components/layout/app-layout';
import { Breadcrumb } from '../../../components/layout/breadcrumb';
import { Button } from '../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Skeleton } from '../../../components/ui/skeleton';
import { usePermissions } from '../../../hooks/use-permissions';
import { api } from '../../../lib/ky';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth.store';

export const Route = createFileRoute('/pledge-studies/$pledgeStudyId/edit')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('pledge:create')) throw redirect({ to: '/dashboard' });
  },
  component: PledgeStudyEditPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface Attachment {
  id: string;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  signedUrl?: string | null;
  uploadedAt: string;
  uploadedBy: { name: string };
}

interface MeasurementPoint {
  id: string;
  measurementPointTypeId: string;
  unitPriceAtTime: string;
  quantity: number;
  measurementPointType: { id: string; name: string; code: string; unitPrice: string } | null;
}

interface PledgeStudyDetail {
  id: string;
  status: string;
  pledgeStudyTypeId: string;
  pledgeStudyType: { id: string; name: string; code: string };
  measurementPoints: MeasurementPoint[];
  attachments: Attachment[];
  request: { id: string; referenceNo: string };
}

interface PledgeStudyType {
  id: string;
  name: string;
  code: string;
  baseAmount: string;
  status: string;
}

interface MeasurementPointType {
  id: string;
  name: string;
  code: string;
  unitPrice: string;
  status: string;
}

// ── API fetch ─────────────────────────────────────────────────────────────────

async function fetchPledgeStudy(id: string): Promise<PledgeStudyDetail> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`/api/v1/pledge-studies/${id}`, {
    headers: { Authorization: `Bearer ${token ?? ''}` },
  });
  if (!res.ok) throw new Error('Failed to load pledge study');
  return res.json();
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function FileIcon({ mimeType }: { mimeType: string }) {
  if (mimeType === 'application/pdf') return <FileText size={16} className="text-red-500" />;
  if (mimeType.startsWith('image/')) return <Image size={16} className="text-blue-500" />;
  return <FileText size={16} className="text-gray-400" />;
}

const ACCEPTED_TYPES = '.pdf,.jpg,.jpeg,.png';
const ACCEPTED_MIME = ['application/pdf', 'image/jpeg', 'image/png'];

// ── Page ──────────────────────────────────────────────────────────────────────

function PledgeStudyEditPage() {
  const { pledgeStudyId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = usePermissions();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [addMpId, setAddMpId] = useState<string>('');

  const {
    data: ps,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['pledgeStudy', pledgeStudyId],
    queryFn: () => fetchPledgeStudy(pledgeStudyId),
  });

  const { data: psTypes } = useQuery({
    queryKey: ['pledgeStudyTypes'],
    queryFn: () =>
      pledgeStudyConfigControllerListPledgeStudyTypes() as unknown as Promise<PledgeStudyType[]>,
  });

  const { data: mpTypes } = useQuery({
    queryKey: ['measurementPointTypes'],
    queryFn: () =>
      pledgeStudyConfigControllerListMeasurementPoints() as unknown as Promise<
        MeasurementPointType[]
      >,
  });

  // Local controlled state for the form
  const [selectedTypeId, setSelectedTypeId] = useState<string>('');
  const [measurementPoints, setMeasurementPoints] = useState<Record<string, number>>({});
  const [initialized, setInitialized] = useState(false);

  // Initialize from loaded data (once)
  if (ps && !initialized) {
    setSelectedTypeId(ps.pledgeStudyTypeId);
    setMeasurementPoints(
      Object.fromEntries(
        ps.measurementPoints.map((mp) => [mp.measurementPointTypeId, mp.quantity]),
      ),
    );
    setInitialized(true);
  }

  const isEditable = ps?.status === 'DRAFT' && can('pledge:create');

  const saveMutation = useMutation({
    mutationFn: () =>
      pledgeStudyControllerUpdate({
        permitId: ps!.request.id,
        requestBody: {
          pledgeStudyTypeId: selectedTypeId,
          measurementPoints: Object.entries(measurementPoints)
            .filter(([, qty]) => qty > 0)
            .map(([id, qty]) => ({ measurementPointTypeId: id, quantity: qty })),
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pledgeStudy', pledgeStudyId] });
      qc.invalidateQueries({ queryKey: ['pledgeStudies'] });
      navigate({ to: '/pledge-studies/$pledgeStudyId', params: { pledgeStudyId } });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (attachmentId: string) =>
      pledgeStudyControllerDeleteAttachment({
        permitId: ps!.request.id,
        attachmentId,
      }),
    onSuccess: () => {
      setDeleteConfirm(null);
      qc.invalidateQueries({ queryKey: ['pledgeStudy', pledgeStudyId] });
    },
  });

  async function handleFiles(files: FileList | null) {
    if (!files || !ps) return;
    setUploadError(null);
    for (const file of Array.from(files)) {
      if (!ACCEPTED_MIME.includes(file.type)) {
        setUploadError(`"${file.name}" is not a supported file type (PDF, JPG, PNG).`);
        continue;
      }
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`api/v1/requests/${ps.request.id}/pledge-study/attachments`, {
          body: fd,
        });
        qc.invalidateQueries({ queryKey: ['pledgeStudy', pledgeStudyId] });
      } catch {
        setUploadError(`Failed to upload "${file.name}". Please try again.`);
      } finally {
        setUploading(false);
      }
    }
  }

  function addMeasurementPoint() {
    if (!addMpId) return;
    setMeasurementPoints((prev) => ({ ...prev, [addMpId]: 1 }));
    setAddMpId('');
  }

  function removeMeasurementPoint(id: string) {
    setMeasurementPoints((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  function setQty(id: string, qty: number) {
    setMeasurementPoints((prev) => ({ ...prev, [id]: Math.max(1, qty || 1) }));
  }

  if (isLoading) {
    return (
      <AppLayout>
        <div className="space-y-4">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-12 w-full rounded-2xl" />
          <Skeleton className="h-48 w-full rounded-2xl" />
          <Skeleton className="h-48 w-full rounded-2xl" />
        </div>
      </AppLayout>
    );
  }

  if (isError || !ps) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <AlertCircle size={40} className="text-error-400" />
          <p className="text-sm font-semibold text-gray-600">Pledge study not found</p>
        </div>
      </AppLayout>
    );
  }

  if (!isEditable) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <Lock size={40} className="text-gray-300" />
          <p className="text-sm font-semibold text-gray-600">This pledge study cannot be edited</p>
          <p className="text-xs text-gray-400">Only draft pledge studies can be edited.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              navigate({ to: '/pledge-studies/$pledgeStudyId', params: { pledgeStudyId } })
            }
          >
            Back to Pledge Study
          </Button>
        </div>
      </AppLayout>
    );
  }

  const breadcrumbItems = [
    { label: 'Pledge & Study', href: '/pledge-studies' },
    { label: ps.request.referenceNo, href: `/pledge-studies/${pledgeStudyId}` },
    { label: 'Edit' },
  ];

  // Measurement point types that haven't been added yet
  const availableMpTypes = (mpTypes ?? []).filter(
    (mpt) => mpt.status === 'ACTIVE' && !(mpt.id in measurementPoints),
  );

  const selectedMpEntries = Object.entries(measurementPoints);

  return (
    <AppLayout>
      <Breadcrumb items={breadcrumbItems} />

      {/* ── Header ── */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 leading-tight">
            Edit Pledge &amp; Study
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">{ps.request.referenceNo}</p>
        </div>
        <div className="flex items-center gap-2.5 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              navigate({ to: '/pledge-studies/$pledgeStudyId', params: { pledgeStudyId } })
            }
          >
            <X size={14} />
            Cancel
          </Button>
          <Button
            variant="success"
            size="sm"
            disabled={!selectedTypeId || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            <Save size={14} />
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {saveMutation.isError && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700">
          <AlertCircle size={15} className="shrink-0" />
          Failed to save. Please try again.
        </div>
      )}

      <div className="space-y-4">
        {/* Study Type */}
        <FormSection
          icon={<BookMarked size={16} className="text-indigo-600" />}
          iconBg="bg-indigo-50 text-indigo-600"
          title="Study Type"
          subtitle="Select the type of pledge study"
        >
          <Select value={selectedTypeId || undefined} onValueChange={(v) => setSelectedTypeId(v)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select study type…" />
            </SelectTrigger>
            <SelectContent>
              {(psTypes ?? [])
                .filter((t) => t.status === 'ACTIVE')
                .map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </FormSection>

        {/* Measurement Points */}
        <FormSection
          icon={<BookMarked size={16} className="text-violet-600" />}
          iconBg="bg-violet-50 text-violet-600"
          title="Measurement Points"
          subtitle="Define the measurement points for this study"
        >
          {selectedMpEntries.length > 0 && (
            <div className="mb-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="pb-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      Name
                    </th>
                    <th className="pb-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      Code
                    </th>
                    <th className="pb-2 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      Unit Price
                    </th>
                    <th className="pb-2 text-center text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      Qty
                    </th>
                    <th className="pb-2 w-8" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {selectedMpEntries.map(([mpTypeId, qty]) => {
                    const mpt = (mpTypes ?? []).find((m) => m.id === mpTypeId);
                    return (
                      <tr key={mpTypeId}>
                        <td className="py-2.5 text-gray-800 font-medium">
                          {mpt?.name ?? mpTypeId}
                        </td>
                        <td className="py-2.5 text-gray-500">{mpt?.code ?? '—'}</td>
                        <td className="py-2.5 text-right text-gray-700">
                          <span className="text-[10px] text-gray-400 mr-0.5">AED</span>
                          {mpt
                            ? Number.parseFloat(mpt.unitPrice).toLocaleString('en-AE', {
                                minimumFractionDigits: 2,
                              })
                            : '—'}
                        </td>
                        <td className="py-2.5">
                          <div className="flex items-center justify-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => setQty(mpTypeId, qty - 1)}
                              disabled={qty <= 1}
                              className="flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600 disabled:opacity-30 transition-colors"
                            >
                              <Minus size={11} />
                            </button>
                            <input
                              type="number"
                              min={1}
                              value={qty}
                              onChange={(e) =>
                                setQty(mpTypeId, Number.parseInt(e.target.value, 10))
                              }
                              className="w-12 rounded-lg border border-gray-200 px-2 py-1 text-center text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"
                            />
                            <button
                              type="button"
                              onClick={() => setQty(mpTypeId, qty + 1)}
                              className="flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600 transition-colors"
                            >
                              <Plus size={11} />
                            </button>
                          </div>
                        </td>
                        <td className="py-2.5">
                          <button
                            type="button"
                            onClick={() => removeMeasurementPoint(mpTypeId)}
                            className="flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-error-200 hover:bg-error-50 hover:text-error-500 transition-colors"
                            title="Remove point"
                          >
                            <X size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Add measurement point */}
          {availableMpTypes.length > 0 && (
            <div className="flex items-center gap-2">
              <Select value={addMpId || undefined} onValueChange={setAddMpId}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Add measurement point…" />
                </SelectTrigger>
                <SelectContent>
                  {availableMpTypes.map((mpt) => (
                    <SelectItem key={mpt.id} value={mpt.id}>
                      {mpt.name} <span className="text-gray-400 ml-1">({mpt.code})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                disabled={!addMpId}
                onClick={addMeasurementPoint}
                className="shrink-0"
              >
                <Plus size={14} />
                Add
              </Button>
            </div>
          )}

          {selectedMpEntries.length === 0 && availableMpTypes.length === 0 && (
            <p className="text-sm text-gray-400 italic">No measurement point types available.</p>
          )}
        </FormSection>

        {/* Attachments */}
        <FormSection
          icon={<CloudUpload size={16} className="text-violet-600" />}
          iconBg="bg-violet-50 text-violet-600"
          title="Attachments"
          subtitle="PDF, JPG, PNG — max 10 MB per file"
        >
          {/* Drop zone */}
          <div
            className={cn(
              'mb-4 flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-all cursor-pointer',
              isDragOver
                ? 'border-indigo-400 bg-indigo-50'
                : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100',
            )}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragOver(false);
              handleFiles(e.dataTransfer.files);
            }}
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white border border-gray-200 shadow-sm">
              {uploading ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
              ) : (
                <CloudUpload size={20} className="text-gray-400" />
              )}
            </div>
            <p className="text-sm font-medium text-gray-600">
              {uploading ? 'Uploading…' : 'Drop files here or click to browse'}
            </p>
            <p className="text-xs text-gray-400">PDF, JPG, PNG supported</p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_TYPES}
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
          </div>

          {uploadError && (
            <div className="mb-3 flex items-center gap-2 rounded-xl border border-error-200 bg-error-50 px-4 py-2.5 text-xs text-error-700">
              <AlertCircle size={13} className="shrink-0" />
              {uploadError}
            </div>
          )}

          {ps.attachments.length > 0 && (
            <div className="space-y-2">
              {ps.attachments.map((att) => (
                <div
                  key={att.id}
                  className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-4 py-3"
                >
                  <FileIcon mimeType={att.mimeType} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{att.fileName}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {fmtSize(att.fileSizeBytes)} · {att.uploadedBy.name} ·{' '}
                      {fmtDate(att.uploadedAt)}
                    </p>
                  </div>
                  {att.signedUrl && (
                    <a
                      href={att.signedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-brand-300 hover:text-brand-500 transition-colors"
                      title="Download"
                    >
                      <Download size={14} />
                    </a>
                  )}
                  {deleteConfirm === att.id ? (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => deleteMutation.mutate(att.id)}
                        disabled={deleteMutation.isPending}
                        className="rounded-lg bg-error-500 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-error-600 transition-colors disabled:opacity-50"
                      >
                        {deleteMutation.isPending ? '…' : 'Delete'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteConfirm(null)}
                        className="rounded-lg border border-gray-200 px-2.5 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setDeleteConfirm(att.id)}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-error-200 hover:bg-error-50 hover:text-error-500 transition-colors"
                      title="Delete attachment"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </FormSection>
      </div>
    </AppLayout>
  );
}
